import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, ChevronLeft } from "lucide-react";

import { Calendar } from "@/shared/components/ui/calendar";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { cn } from "@/shared/utils/core";

interface DatePickerProps {
  id?: string;
  value?: Date;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  dateString?: string; // YYYY-MM-DD format
  onDateStringChange?: (dateString: string | undefined) => void;
  isBirthDate?: boolean;
  inputOnly?: boolean;
}

function parseLocalDate(dateStr: string | undefined): Date | undefined {
  if (!dateStr) return undefined;
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return undefined;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
}

function formatDateString(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

function formatDisplay(date: Date | undefined): string {
  if (!date || isNaN(date.getTime())) return "";
  return format(date, "MM/dd/yyyy");
}

// Always strip to digits and auto-insert slashes for consistent formatting.
function formatTypedInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

// Accept m/d/yyyy, mm/dd/yyyy, m-d-yyyy, m.d.yyyy, 2-digit years, etc.
function parseTypedDate(input: string): Date | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  const match = trimmed.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (!match) return undefined;
  const [, month, day, yearStr] = match;
  const m = Number(month);
  const d = Number(day);
  let y = Number(yearStr);
  if (yearStr.length === 2) y = y <= 30 ? 2000 + y : 1900 + y;
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1000 || y > 9999) return undefined;
  const date = new Date(y, m - 1, d, 12, 0, 0, 0);
  if (date.getMonth() !== m - 1 || date.getDate() !== d) return undefined;
  return date;
}

type BirthDateStep = "decade" | "year" | "month" | "day";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getDecades(): number[] {
  const currentDecade = Math.floor(new Date().getFullYear() / 10) * 10;
  const decades: number[] = [];
  for (let decade = 1920; decade <= currentDecade; decade += 10) {
    decades.push(decade);
  }
  return decades;
}

function getYearsInDecade(decade: number): number[] {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let year = decade; year < decade + 10 && year <= currentYear; year++) {
    years.push(year);
  }
  return years;
}

function getDaysInMonth(year: number, month: number): number[] {
  return Array.from({ length: new Date(year, month + 1, 0).getDate() }, (_, i) => i + 1);
}

export function DatePicker({
  id,
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  disabled = false,
  className = "",
  dateString,
  onDateStringChange,
  isBirthDate = false,
  inputOnly = false,
}: DatePickerProps) {
  const useStringMode = onDateStringChange !== undefined;
  const displayDate = useStringMode ? parseLocalDate(dateString) : value;
  const inputRef = useRef<HTMLInputElement>(null);

  const [inputValue, setInputValue] = useState(() => formatDisplay(displayDate));
  const [isOpen, setIsOpen] = useState(false);

  const [calendarMonth, setCalendarMonth] = useState<Date>(displayDate ?? new Date());
  const [birthDateStep, setBirthDateStep] = useState<BirthDateStep>("decade");
  const [selectedDecade, setSelectedDecade] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  // Use stable primitives as deps — Date objects are new each render from parseLocalDate
  const displayKey = useStringMode ? dateString : value?.getTime();
  useEffect(() => {
    setInputValue(formatDisplay(displayDate));
    if (displayDate) setCalendarMonth(displayDate);
  }, [displayKey]);

  const handleSelect = (date: Date | undefined) => {
    if (useStringMode) {
      onDateStringChange(date ? formatDateString(date) : undefined);
    } else {
      onChange(date);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    // Try parsing raw input directly — only for paste (must have 4-digit year)
    const pasteMatch = raw.match(/(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
    if (pasteMatch) {
      const directParse = parseTypedDate(raw);
      if (directParse) {
        setInputValue(formatDisplay(directParse));
        handleSelect(directParse);
        return;
      }
    }

    // Pad single-digit month/day when user types a separator (e.g., "6/" -> "06/")
    const padded = raw
      .replace(/^(\d)[/\-.](\d)/, "0$1/0$2")
      .replace(/^(\d)[/\-.]/, "0$1/")
      .replace(/^(\d{2})\/(\d)[/\-.]/, "$1/0$2/");

    // Strip to digits and auto-format for keystroke-by-keystroke typing
    const formatted = formatTypedInput(padded);
    setInputValue(formatted);
    if (!formatted) {
      handleSelect(undefined);
    } else if (formatted.length === 10) {
      // Only commit when fully typed: MM/DD/YYYY (10 chars)
      const date = parseTypedDate(formatted);
      if (date) handleSelect(date);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Backspace" && e.key !== "Delete") return;

    const input = e.currentTarget;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;

    // If there's a text selection, let the browser handle it naturally
    if (start !== end) return;

    if (e.key === "Backspace" && start > 0) {
      e.preventDefault();
      // Skip over slashes when backspacing
      const deletePos = inputValue[start - 1] === "/" ? start - 2 : start - 1;
      if (deletePos < 0) return;
      const next = inputValue.slice(0, deletePos) + inputValue.slice(deletePos + 1);
      const formatted = formatTypedInput(next);
      setInputValue(formatted);
      if (!formatted) handleSelect(undefined);
      // Place cursor at the right position
      const cursorPos = Math.min(deletePos, formatted.length);
      requestAnimationFrame(() => input.setSelectionRange(cursorPos, cursorPos));
    } else if (e.key === "Delete" && start < inputValue.length) {
      e.preventDefault();
      // Skip over slashes when deleting forward
      const deletePos = inputValue[start] === "/" ? start + 1 : start;
      if (deletePos >= inputValue.length) return;
      const next = inputValue.slice(0, deletePos) + inputValue.slice(deletePos + 1);
      const formatted = formatTypedInput(next);
      setInputValue(formatted);
      if (!formatted) handleSelect(undefined);
      requestAnimationFrame(() => input.setSelectionRange(start, start));
    }
  };

  const handleInputBlur = () => {
    if (inputOnly) return;
    if (inputValue && !parseTypedDate(inputValue)) {
      setInputValue(formatDisplay(displayDate));
    }
  };

  const handleInputClick = () => {
    if (!disabled && !inputOnly) setIsOpen(true);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setBirthDateStep("decade");
      setSelectedDecade(null);
      setSelectedYear(null);
      setSelectedMonth(null);
    }
  };

  const handleDecadeSelect = (decade: number) => {
    setSelectedDecade(decade);
    setBirthDateStep("year");
  };
  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setBirthDateStep("month");
  };
  const handleMonthSelect = (month: number) => {
    setSelectedMonth(month);
    setBirthDateStep("day");
  };

  const handleDaySelect = (day: number) => {
    if (selectedYear !== null && selectedMonth !== null) {
      handleSelect(new Date(selectedYear, selectedMonth, day, 12, 0, 0, 0));
      setIsOpen(false);
    }
  };

  const handleBack = () => {
    if (birthDateStep === "year") {
      setBirthDateStep("decade");
      setSelectedDecade(null);
    } else if (birthDateStep === "month") {
      setBirthDateStep("year");
      setSelectedYear(null);
    } else if (birthDateStep === "day") {
      setBirthDateStep("month");
      setSelectedMonth(null);
    }
  };

  const renderBreadcrumb = () => {
    const crumbs: { label: string; onClick?: () => void }[] = [];
    if (selectedDecade !== null) {
      crumbs.push({
        label: `${selectedDecade}s`,
        onClick:
          birthDateStep !== "year"
            ? () => {
                setBirthDateStep("year");
                setSelectedYear(null);
                setSelectedMonth(null);
              }
            : undefined,
      });
    }
    if (selectedYear !== null) {
      crumbs.push({
        label: `${selectedYear}`,
        onClick:
          birthDateStep !== "month"
            ? () => {
                setBirthDateStep("month");
                setSelectedMonth(null);
              }
            : undefined,
      });
    }
    if (selectedMonth !== null) {
      crumbs.push({ label: MONTHS[selectedMonth].slice(0, 3) });
    }
    if (crumbs.length === 0) return null;
    return (
      <div className="flex items-center gap-1.5 mb-3">
        <button
          onClick={handleBack}
          className="h-6 w-6 flex items-center justify-center rounded-sm hover:bg-stone-100 transition-colors"
        >
          <ChevronLeft className="h-4 w-4 text-stone-500" />
        </button>
        <div className="flex items-center gap-1">
          {crumbs.map((crumb, index) => (
            <div key={index} className="flex items-center">
              {index > 0 && <span className="text-stone-300 mx-1">/</span>}
              {crumb.onClick ? (
                <button
                  onClick={crumb.onClick}
                  className="text-sm text-stone-500 hover:text-stone-700 transition-colors"
                >
                  {crumb.label}
                </button>
              ) : (
                <span className="text-sm font-medium text-stone-700">{crumb.label}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBirthDateContent = () => {
    const gridButtonClass =
      "h-9 text-sm font-normal text-stone-700 hover:bg-stone-100 rounded-md transition-colors";
    return (
      <div className="p-3 w-[280px]">
        {renderBreadcrumb()}
        {birthDateStep === "decade" && (
          <div className="grid grid-cols-3 gap-1.5">
            {getDecades().map((decade) => (
              <button
                key={decade}
                onClick={() => handleDecadeSelect(decade)}
                className={gridButtonClass}
              >
                {decade}s
              </button>
            ))}
          </div>
        )}
        {birthDateStep === "year" && selectedDecade !== null && (
          <div className="grid grid-cols-5 gap-1.5">
            {getYearsInDecade(selectedDecade).map((year) => (
              <button key={year} onClick={() => handleYearSelect(year)} className={gridButtonClass}>
                {year}
              </button>
            ))}
          </div>
        )}
        {birthDateStep === "month" && selectedYear !== null && (
          <div className="grid grid-cols-3 gap-1.5">
            {MONTHS.map((month, index) => (
              <button
                key={month}
                onClick={() => handleMonthSelect(index)}
                className={gridButtonClass}
              >
                {month.slice(0, 3)}
              </button>
            ))}
          </div>
        )}
        {birthDateStep === "day" && selectedYear !== null && selectedMonth !== null && (
          <div className="grid grid-cols-7 gap-1">
            {getDaysInMonth(selectedYear, selectedMonth).map((day) => (
              <button
                key={day}
                onClick={() => handleDaySelect(day)}
                className="h-9 w-9 text-sm font-normal text-stone-700 hover:bg-stone-100 rounded-md transition-colors"
              >
                {day}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (inputOnly) {
    return (
      <div
        className={cn(
          "flex h-10 w-full items-center rounded-md border bg-white px-3 text-sm",
          "border-primary focus-within:border-focus",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 bg-transparent outline-none placeholder:text-tertiary min-w-0"
        />
      </div>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <div
          className={cn(
            "flex h-10 w-full items-center rounded-md border bg-white px-3 text-sm",
            isOpen ? "border-focus" : "border-primary",
            "focus-within:border-focus",
            disabled && "cursor-not-allowed opacity-50",
            className,
          )}
        >
          <input
            ref={inputRef}
            id={id}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleInputBlur}
            onClick={handleInputClick}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 bg-transparent outline-none placeholder:text-tertiary min-w-0"
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className="ml-2 shrink-0 text-stone-400 hover:text-stone-600 disabled:cursor-not-allowed"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {isBirthDate ? (
          renderBirthDateContent()
        ) : (
          <Calendar
            mode="single"
            captionLayout="dropdown"
            selected={displayDate}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            required
            onSelect={(date) => {
              handleSelect(date);
              setIsOpen(false);
            }}
            fromYear={1900}
            toYear={new Date().getFullYear() + 100}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
